/**
 * daily_shipments.repository — 일별 출하 집계 read.
 *
 * 문서 ID = "YYYY-MM-DD" 패턴 → documentId() range query로 연간/월간 가져옴.
 */
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import { coerceDailyShipment, type DailyShipment } from "@/domain/daily_shipments";

const COL = "daily_shipments";

export const dailyShipmentsRepo = {
  /** 단건 (없으면 null) */
  async getByDate(dateStr: string): Promise<DailyShipment | null> {
    const ref = doc(db, tcol(COL), dateStr);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return coerceDailyShipment(snap.id, snap.data());
  },

  /** 단건 실시간 구독 (없으면 null) */
  watchByDate(
    dateStr: string,
    cb: (s: DailyShipment | null) => void,
    onError?: (e: Error) => void,
  ): () => void {
    return onSnapshot(
      doc(db, tcol(COL), dateStr),
      (snap) => cb(snap.exists() ? coerceDailyShipment(snap.id, snap.data()) : null),
      onError,
    );
  },

  /** 연간 (YYYY-01-01 ~ YYYY-12-31 documentId range) */
  async listByYear(year: number): Promise<DailyShipment[]> {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    const q = query(
      collection(db, tcol(COL)),
      where(documentId(), ">=", start),
      where(documentId(), "<=", end),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => coerceDailyShipment(d.id, d.data()));
  },

  /** 기간 [from..to] (documentId range, 문서 ID = 날짜). 내 업무 홈 생산 일정(예정)용 */
  async listBetween(from: string, to: string): Promise<DailyShipment[]> {
    const q = query(
      collection(db, tcol(COL)),
      where(documentId(), ">=", from),
      where(documentId(), "<=", to),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => coerceDailyShipment(d.id, d.data()));
  },

  /** 선택 연도의 출하 집계 실시간 구독 (호출한 화면에서 구독 해제) */
  watchByYear(
    year: number,
    cb: (rows: DailyShipment[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const q = query(
      collection(db, tcol(COL)),
      where(documentId(), ">=", `${year}-01-01`),
      where(documentId(), "<=", `${year}-12-31`),
    );
    return onSnapshot(
      q,
      (snap) => cb(snap.docs.map((d) => coerceDailyShipment(d.id, d.data()))),
      onError,
    );
  },
};
